import { Link } from '@tanstack/react-router'
import { ExternalLink, Truck, X } from 'lucide-react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { formatMoney } from '@/lib/utils'
import type {
  CatalogSearchProduct,
  ShopifyQuantityPricing,
  ShopifySearchProduct,
  StorefrontScope,
} from './storefront-api'
import { normalizeProductTitle, productSaleUnit, volumeDiscountLevels } from './storefront-format'

// The AI overview's products (USA-1249) carry no ship time yet.
type CardProduct = ShopifySearchProduct & Partial<Pick<CatalogSearchProduct, 'ship_days'>>

function ShippingBadge({ product }: { product: CardProduct }) {
  if (!product.free_shipping && !product.small_parcel_free_freight_eligible) return null
  return (
    <span className="sf-card__label">
      {product.free_shipping ? 'Free Shipping' : 'Free shipping eligible'}
    </span>
  )
}

function VolumeDiscount({
  pricing,
  title,
  unitsPerCase,
}: {
  pricing: ShopifyQuantityPricing
  title: string
  unitsPerCase: string | null
}) {
  const saleUnit = productSaleUnit(pricing.quantityOnePrice, unitsPerCase)
  const sellsByCase = saleUnit.caseQuantity !== null
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="sf-volume__trigger">
          See volume discount
        </button>
      </DialogTrigger>
      <DialogContent showCloseButton={false} className="storefront sf-volume__dialog">
        <div className="sf-volume__header">
          <div>
            <DialogTitle className="sf-volume__title">Volume discount</DialogTitle>
            <p className="sf-volume__subtitle">
              {sellsByCase ? `Prices per case of ${saleUnit.caseQuantity}` : 'Prices per item'}
            </p>
          </div>
          <DialogClose className="sf-volume__close" aria-label="Close volume discount">
            <X size={18} />
          </DialogClose>
        </div>
        <ol className="sf-volume__tiers" aria-label={`Quantity price tiers for ${title}`}>
          {volumeDiscountLevels(pricing).map((level) => {
            const tierUnit = productSaleUnit(level.unitPrice, unitsPerCase)
            const plus = level.minimumQuantity > 1 ? '+' : ''
            const threshold = sellsByCase
              ? `${level.minimumQuantity}${plus} ${level.minimumQuantity === 1 ? 'case' : 'cases'}`
              : `${level.minimumQuantity}${plus}`
            return (
              <li key={level.minimumQuantity}>
                <span className="sf-volume__threshold">{threshold}</span>
                <span className="sf-volume__price">
                  <strong>
                    {formatMoney(level.unitPrice)}/{sellsByCase ? 'case' : 'each'}
                  </strong>
                  {tierUnit.eachUnitPrice ? (
                    <span>{formatMoney(tierUnit.eachUnitPrice)}/each</span>
                  ) : null}
                </span>
              </li>
            )
          })}
        </ol>
      </DialogContent>
    </Dialog>
  )
}

export function StorefrontProductCard({
  product,
  onScope,
}: {
  product: CardProduct
  onScope: (scope: StorefrontScope) => void
}) {
  const title = normalizeProductTitle(product.title)
  const pricing = product.quantity_pricing ?? null
  const displayPrice = pricing?.quantityOnePrice ?? product.price
  const saleUnit = productSaleUnit(displayPrice, product.units_per_case)
  const caseQuantity = saleUnit.caseQuantity

  return (
    <article className="sf-card" data-e2e="product-card">
      <div className="sf-card__image">
        <ShippingBadge product={product} />
        {caseQuantity !== null ? (
          <span className="sf-card__case">
            <b>{caseQuantity}</b>
            <span>case</span>
          </span>
        ) : null}
        <Link
          to="/products/$productId"
          params={{ productId: product.uuid }}
          className="sf-card__image-link"
          tabIndex={-1}
          aria-hidden="true"
        >
          {product.thumbnail ? (
            <img src={product.thumbnail} alt="" width={300} height={300} loading="lazy" />
          ) : (
            <span className="sf-card__placeholder">{product.model.charAt(0) || '?'}</span>
          )}
        </Link>
      </div>
      <div className="sf-card__body">
        <button
          type="button"
          className="sf-card__vendor"
          onClick={() =>
            onScope({
              key: 'manufacturer',
              value: product.vendor.shopify_id,
              label: product.vendor.title,
            })
          }
        >
          {product.vendor.title}
        </button>
        <div className="sf-card__sku">
          <span className="sf-card__sku-label">SKU</span>
          <span>{product.model}</span>
        </div>
        <h3 className="sf-card__title">
          <Link to="/products/$productId" params={{ productId: product.uuid }}>
            {title}
          </Link>
        </h3>
        <div className="sf-card__price">
          <span className="sf-price">{formatMoney(displayPrice)}</span>
          {caseQuantity !== null ? (
            <>
              <span className="sf-price__unit sf-price__unit--case">/case of {caseQuantity}</span>
              {saleUnit.eachUnitPrice ? (
                <span className="sf-price__each">
                  <b>{formatMoney(saleUnit.eachUnitPrice)}</b>
                  <span>each</span>
                </span>
              ) : null}
            </>
          ) : (
            <span className="sf-price__unit">/each</span>
          )}
          {pricing ? (
            <VolumeDiscount pricing={pricing} title={title} unitsPerCase={product.units_per_case} />
          ) : null}
        </div>
        {product.ship_days != null ? (
          <p className="sf-card__ship">
            <Truck size={14} strokeWidth={1.75} aria-hidden="true" />
            Ships in {product.ship_days} business {product.ship_days === 1 ? 'day' : 'days'}
          </p>
        ) : null}
        {/* eslint-disable-next-line no-restricted-syntax -- external storefront URL */}
        <a
          href={product.url}
          target="_blank"
          rel="noopener noreferrer"
          className="sf-button sf-button--primary sf-card__store-link"
        >
          View in store
          <ExternalLink size={14} aria-hidden="true" />
        </a>
      </div>
    </article>
  )
}
